import { createContext, useContext, useState, useEffect, useRef } from "react";
import {
  getRangeFromCategory,
} from "../../components/MetricsPanel/PanelContents/components/Charts/helpers";
import {
  getUnitsForSingleChart,
  calculateBaselineData,
} from "../../components/MetricsPanel/PanelContents/helpers";
import {
  margin,
  width,
  height,
} from "../../components/MetricsPanel/PanelContents/components/Charts/constants";
import * as components from "../../components/MetricsPanel/PanelContents/components";
import * as constants from "../../constants";
import { initialAreaUnit } from "../../components/MetricsPanel/PanelContents/helpers/getUnitsForSingleChart";

import { useCurrentSelection } from "../SelectionProvider";

const ChartContext = createContext();

export const ChartProvider = ({ product, children }) => {
  const { currentSelection } = useCurrentSelection();
  const chartContainerRef = useRef(null);
  const chartData = currentSelection.chartData[product];
  const baselineData = currentSelection.baselineData;

  // chart states
  const [units, setUnits] = useState(initialAreaUnit);
  const [xAxisLimits, setXAxisLimits] = useState(getRangeFromCategory(chartData));
  const [chartRange, setChartRange] = useState(xAxisLimits);
  const [chartWidth, setChartWidth] = useState(0);
  const [chartHeight, setChartHeight] = useState(0);

  const updateBaselineData = (newUnits, newRange) => {
    // Compute the new averages based on the new units
    const newAverages = calculateBaselineData(
      chartData,
      baselineData,
      newRange,
      initialAreaUnit
    );

    currentSelection.setAllMapData((prevAllPanelData) => {
      const updatedData = {
        ...prevAllPanelData,
        baselineData: {
          ...prevAllPanelData.baselineData,
          ...newAverages,
        },
      };

      return updatedData;
    });
  };

  const updateChart = (newData) => {
    if (newData) {
      const ranges = getRangeFromCategory(newData);
      setChartRange(ranges); // Update the range in the parent component

      const newUnits = getUnitsForSingleChart(newData, xAxisLimits);
      setUnits(newUnits);

      updateBaselineData(newUnits, ranges);
    }
  };

  // Callback function to handle the selected range
  const onFilterChange = (id, newRange) => {
    // Compute the new units based on the updated xAxisLimits
    const newUnits = getUnitsForSingleChart(chartData, newRange);
    setUnits(newUnits);

    updateBaselineData(newUnits, newRange);

    // Update the state in a batch
    setUnits(newUnits);
    setXAxisLimits(newRange); // Update the range in the parent component
  };

  useEffect(() => {
    updateChart(chartData);
  }, [chartData]);

  useEffect(() => {
    const marginX = margin.left + margin.right;
    const marginY = margin.top + margin.bottom;
    const chartContainerWidth =
      chartContainerRef.current?.clientWidth - marginX;
    const chartContainerHeight =
      chartContainerRef.current?.clientHeight - marginY;

    setChartWidth(chartContainerWidth > width ? chartContainerWidth : width);
    setChartHeight(
      chartContainerHeight > height ? chartContainerHeight : height
    );
  }, [chartContainerRef.current]);

  useEffect(() => {
    if (chartData) {
      setXAxisLimits(getRangeFromCategory(chartData)); 
      setChartRange(xAxisLimits);
      updateChart(chartData);
    }
  }, [chartData]);

  const chartSectionProps = {
    modalContent: constants.panelConfig[product].infoTooltip,
    chart: <components.Chart />,
    category: product,
  };

  const currentChart = {
    units,
    setUnits,
    xAxisLimits,
    chartRange,
    setXAxisLimits,
    onFilterChange,
    chartContainerRef,
    chartWidth,
    chartHeight,
    chartSectionProps,
    category: product,
    chartData: currentSelection.chartData,
    baselineInfo: currentSelection.baselineInfo,
    baselineData: currentSelection.baselineData,
  };
  return (
    <ChartContext.Provider value={{ currentChart }}>
      {children}
    </ChartContext.Provider>
  );
};

export const useCurrentChart = () => {
  const context = useContext(ChartContext);

  if (context === undefined) {
    throw new Error("useCurrentChart must be used within a ChartProvider");
  }

  return context;
};
